import { Component, ChangeDetectorRef, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { Calendar } from '../calendar/calendar';
import { Loader } from '../shared/loader/loader';
import { AuthContextService, PatientInfo } from '../shared/auth-context.service';
import { ApiService } from '../shared/services/api.service';

@Component({
  selector: 'app-nc-psychotherapy-waitlist',
  imports: [CommonModule, FormsModule, Navbar, Footer, Calendar, Loader],
  templateUrl: './nc-psychotherapy-waitlist.html',
  styleUrl: './nc-psychotherapy-waitlist.css'
})
export class NCPsychotherapyWaitlist implements OnInit {
  constructor(private router: Router, private api: ApiService, private cdr: ChangeDetectorRef, private auth: AuthContextService) {}

  private pushDataLayerEvent(eventName: string, payload: Record<string, unknown> = {}): void {
    const normalizedEventName = String(eventName || '').trim();
    if (!normalizedEventName) return;
    try {
      const dataLayer = (window as any).dataLayer = (window as any).dataLayer || [];
      dataLayer.push({
        event: normalizedEventName,
        ...payload
      });
    } catch {}
  }

  ngOnInit(): void {
    this.prefillFromPatient();
    try { sessionStorage.removeItem('flow.wl.entry'); } catch {}
    try {
      const url = this.router.url || '';
      if (url.includes('/waiting-list-confirmation')) {
        this.showSuccessModal = true;
        this.pushDataLayerEvent('ols_waiting_list_submission');
        try { this.cdr.detectChanges(); } catch {}
        setTimeout(() => this.router.navigate(['/scheduler'], { replaceUrl: true }), 5000);
      } else {
        this.pushDataLayerEvent('ols_waiting_list_shown');
      }
    } catch {}
  }

  showFormModal = false;
  showSuccessModal = false;
  isSubmitting = false;
  ipAddress = '';
  fullName = '';
  phoneNumber = '';
  emailAddress = '';
  birthDate = '';
  emailConsent = false;
  formErrors = { fullName: '', phoneNumber: '', emailAddress: '', birthDate: '' } as any;
  submitError = '';

  onReturn() { this.router.navigate(['/scheduler'], { replaceUrl: true }); }

  onJoin() {
    this.pushDataLayerEvent('ols_add_waiting_list_click');
    this.prefillFromPatient();
    this.showFormModal = true;
    this.pushDataLayerEvent('ols_waiting_list_form_shown');
    try { this.cdr.detectChanges(); } catch {}
    this.fetchIp();
  }

  closeFormModal() { this.showFormModal = false; }

  private fetchIp() {
    try {
      fetch('https://api.ipify.org?format=json').then(r => r.json()).then(j => { this.ipAddress = (j?.ip || '').toString(); try { localStorage.setItem('net.publicIp', this.ipAddress); } catch {} }).catch(() => {});
    } catch {}
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) && email.length <= 40;
  }

  private isValidPhoneNumber(phone: string): boolean {
    const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
    return phoneRegex.test(phone);
  }

  private validate(): boolean {
    this.formErrors = { fullName: '', phoneNumber: '', emailAddress: '', birthDate: '' };
    let ok = true;
    if (!this.fullName.trim()) { this.formErrors.fullName = 'Please enter your full name'; ok = false; }
    else if (this.fullName.trim().length > 40) { this.formErrors.fullName = 'Maximum 40 characters allowed'; ok = false; }
    if (!this.phoneNumber.trim()) { this.formErrors.phoneNumber = 'Please enter your phone number'; ok = false; }
    else if (!this.isValidPhoneNumber(this.phoneNumber)) { this.formErrors.phoneNumber = 'Please enter a valid phone number'; ok = false; }
    if (!this.emailAddress.trim()) { this.formErrors.emailAddress = 'Please enter your email address'; ok = false; }
    else if (this.emailAddress.trim().length > 40) { this.formErrors.emailAddress = 'Maximum 40 characters allowed'; ok = false; }
    else if (!this.isValidEmail(this.emailAddress)) { this.formErrors.emailAddress = 'Please enter a valid email address'; ok = false; }
    if (!this.birthDate) { this.formErrors.birthDate = 'Please enter your birth date'; ok = false; }
    return ok;
  }

  private formatPhoneDigits(digits: string): string {
    const d = (digits || '').replace(/\D/g, '').slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  }

  private caretFromDigits(formatted: string, digitsBefore: number): number {
    if (digitsBefore <= 0) return 0;
    if (digitsBefore <= 3) return digitsBefore;
    if (digitsBefore <= 6) return digitsBefore + 1;
    return digitsBefore + 2;
  }

  private prefillFromPatient() {
    try {
      const p = this.auth.getPatientInfo() as PatientInfo | null;
      if (!p) return;
      const first = (p.legalFirstName || '').trim();
      const last = (p.legalLastName || '').trim();
      const name = `${last} ${first}`.trim().replace(/\s+/g, ' ');
      if (name) this.fullName = name;
      if (p.emailAddress) this.emailAddress = (p.emailAddress || '').toString();
      if (p.dateOfBirth) this.birthDate = (p.dateOfBirth || '').toString();
      const pn = (p.phoneNumber || '').toString();
      if (pn) this.phoneNumber = this.formatPhoneDigits(pn);
    } catch {}
  }

  onPhoneInput(event: Event): void {
    const el = event.target as HTMLInputElement;
    const raw = el.value || '';
    const selStart = el.selectionStart ?? raw.length;
    const digitsBefore = raw.slice(0, selStart).replace(/\D/g, '').length;
    const formatted = this.formatPhoneDigits(raw);
    el.value = formatted;
    this.phoneNumber = formatted;
    const newCaret = this.caretFromDigits(formatted, digitsBefore);
    setTimeout(() => el.setSelectionRange(newCaret, newCaret), 0);
  }

  onPhoneKeydown(event: KeyboardEvent): void {
    const el = event.target as HTMLInputElement;
    const value = el.value || '';
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;

    if (event.key === 'Backspace') {
      event.preventDefault();
      const digits = value.replace(/\D/g, '');
      const digitsBefore = value.slice(0, start).replace(/\D/g, '').length;
      if (digitsBefore === 0 && start === 0 && end === 0) return;
      let newDigits: string;
      if (start !== end) {
        const selStartDigits = value.slice(0, start).replace(/\D/g, '').length;
        const selEndDigits = value.slice(0, end).replace(/\D/g, '').length;
        newDigits = digits.slice(0, selStartDigits) + digits.slice(selEndDigits);
        const newCaretDigits = selStartDigits;
        const formatted = this.formatPhoneDigits(newDigits);
        el.value = formatted;
        this.phoneNumber = formatted;
        const caretPos = this.caretFromDigits(formatted, newCaretDigits);
        setTimeout(() => el.setSelectionRange(caretPos, caretPos), 0);
        return;
      }
      const removeIndex = Math.max(0, digitsBefore - 1);
      newDigits = digits.slice(0, removeIndex) + digits.slice(removeIndex + 1);
      const formatted = this.formatPhoneDigits(newDigits);
      el.value = formatted;
      this.phoneNumber = formatted;
      const newCaret = this.caretFromDigits(formatted, removeIndex);
      setTimeout(() => el.setSelectionRange(newCaret, newCaret), 0);
      return;
    }

    if ([9,27,13,46].includes(event.keyCode) || (event.ctrlKey && [65,67,86,88].includes(event.keyCode)) || (event.keyCode >= 35 && event.keyCode <= 39)) return;
    const isMainNumber = !event.shiftKey && event.keyCode >= 48 && event.keyCode <= 57;
    const isNumpadNumber = event.keyCode >= 96 && event.keyCode <= 105;
    if (!isMainNumber && !isNumpadNumber) event.preventDefault();
  }

  submitWaitlist() {
    this.pushDataLayerEvent('ols_join_waiting_list_click');
    if (this.isSubmitting) return;
    if (!this.validate()) return;
    try { if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('flow.bypass') === '1') { this.onSubmitSuccess(); return; } } catch {}
    const now = new Date();
    const tz = 'America/New_York';
    const receivedDateStr = new Intl.DateTimeFormat('en-US', { timeZone: tz, month: '2-digit', day: '2-digit', year: 'numeric' }).format(now);
    const receivedTimeStr = new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true }).format(now);
    const p = this.auth.getPatientInfo() as PatientInfo | null;
    const nameText = (this.fullName || '').trim();
    let firstName = '';
    let lastName = '';
    if (nameText.includes(',')) {
      const parts = nameText.split(',');
      lastName = (parts[0] || '').trim();
      firstName = (parts.slice(1).join(',') || '').trim();
    } else {
      const parts = nameText.split(/\s+/);
      if (parts.length === 1) firstName = parts[0] || '';
      else { firstName = parts[0] || ''; lastName = parts.slice(1).join(' ') || ''; }
    }
    if (p) {
      if (!firstName) firstName = (p.legalFirstName || '').trim();
      if (!lastName) lastName = (p.legalLastName || '').trim();
    }
    const birthsex = ((p?.gender || '').toUpperCase().startsWith('M') ? 'M' : (p?.gender || '').toUpperCase().startsWith('F') ? 'F' : 'U');
    const stateCode = (p?.state || '').toString();
    const payType = p?.insurance?.payType === 'insurance' ? 'with insurance' : 'self pay';
    const utm_source = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.utm_source')) || '';
    const utm_medium = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.utm_medium')) || '';
    const utm_campaign = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.utm_campaign')) || '';
    const utm_term = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.utm_term')) || '';
    const utm_content = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.utm_content')) || '';
    const gclid = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.gclid')) || '';
    const gad_source = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.gad_source')) || '';
    const gad_campaignid = (typeof localStorage !== 'undefined' && localStorage.getItem('utm.gad_campaignid')) || '';
    const variant = (typeof localStorage !== 'undefined' && (localStorage.getItem('variant') || localStorage.getItem('Version') || localStorage.getItem('version') || localStorage.getItem('ab_variant') || localStorage.getItem('abVersion') || localStorage.getItem('ab_variant_name'))) || 'B';
    const payload: Record<string, string> = {
      fullName: this.fullName.trim(),
      phoneNumber: this.phoneNumber.trim(),
      email: this.emailAddress.trim(),
      dob: this.birthDate,
      source: 'Online Intake',
      receivedDate: receivedDateStr,
      receivedTime: receivedTimeStr,
      ipAddress: this.ipAddress || '',
      Full_Name: this.fullName.trim(),
      Phone_number: this.phoneNumber.trim(),
      DOB: this.birthDate,
      ip_address: this.ipAddress || '',
      Source: 'Online Intake',
      Recived_date: receivedDateStr,
      Recived_time: receivedTimeStr,
      Date_received: receivedDateStr,
      Time_received: receivedTimeStr,
      Patient_id: '',
      First_name: firstName,
      Middle_name: '',
      Last_name: lastName,
      Birthsex: birthsex,
      Phone: this.phoneNumber.trim(),
      Email: this.emailAddress.trim(),
      Scheduled: 'No',
      Appointment_date: '',
      Preferred_provider: '',
      IP_address: this.ipAddress || '',
      Version: variant,
      variant,
      'Google Click ID': gclid,
      Gclid: gclid,
      Gad_campaignid: gad_campaignid,
      gad_source,
      utm_medium,
      utm_campaign,
      utm_term,
      utm_content,
      utm_source,
      Email_received: this.emailConsent ? 'true' : 'false',
    };
    if (stateCode) payload['State'] = stateCode;
    try { localStorage.setItem('sheet.Date_received', receivedDateStr); } catch {}
    try { localStorage.setItem('sheet.Time_received', receivedTimeStr); } catch {}
    this.isSubmitting = true;
    this.submitError = '';
    this.api.dispatch<any>('/waitlist', payload).subscribe({
      next: () => this.onSubmitSuccess(),
      error: () => {
        this.isSubmitting = false;
        this.submitError = 'Unable to add you to the waiting list. Please try again.';
        try { this.cdr.detectChanges(); } catch {}
        setTimeout(() => { this.submitError = ''; try { this.cdr.detectChanges(); } catch {} }, 4000);
      }
    });
  }

  private onSubmitSuccess() {
    this.isSubmitting = false;
    this.showFormModal = false;
    try { sessionStorage.setItem('flow.wl.success', '1'); } catch {}
    this.router.navigate(['/waiting-list-confirmation']);
  }
}


